import { HttpError, AuthTypes, ApiInterfaces } from '@flashbacktech/flashbackclient/api';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import React from 'react';

// Non-hook version for use inside AuthProvider (before context is available)
export function createWithTokenRefresh(
  apiClient: any,
  authState: AuthTypes.AuthState | null,
  setAuthState: (authState: AuthTypes.AuthState) => void,
  navigate: (path: string) => void
) {
  return async function withTokenRefresh<T>(apiCall: () => Promise<T>): Promise<T> {
    try {
      return await apiCall();
    } catch (error: any) {
      // When MFA verification is pending the token is temporary — don't try to
      // refresh it and don't navigate away, just let the error propagate so the
      // MFA flow can complete undisturbed.
      const isMfaPending = (() => { try { return sessionStorage.getItem('mfa_pending') === 'true'; } catch { return false; } })();
      if (isMfaPending) throw error;

      const is401 =
        (error instanceof HttpError && error.status === 401) ||
        (error &&
          typeof error === 'object' &&
          'status' in error &&
          (error as any).status === 401);
      if (is401 && authState?.refreshToken) {
        try {
          const newToken = await apiClient.refreshToken(
            authState.refreshToken,
            authState.provider || ApiInterfaces.ProviderType.GOOGLE
          );

          if (
            newToken &&
            'token' in newToken &&
            'accessToken' in newToken &&
            'refreshToken' in newToken
          ) {
            const { token, accessToken, refreshToken, expiresAt } = newToken;

            // Mettre à jour l'état d'authentification avec les nouveaux tokens
            const updatedAuthState = {
              ...authState,
              token: token,
              accessToken: accessToken,
              refreshToken: refreshToken,
              expiresAt: expiresAt,
            };
            setAuthState(updatedAuthState);
            // Utiliser le nouveau token pour l'API client, pas l'ancien
            apiClient.setAuthToken(token);
          } else {
            navigate('/');
            throw new Error('Authentication failed');
          }
          return await apiCall();
        } catch (refreshError) {
          navigate('/');
          throw new Error('Authentication failed');
        }
      }
      throw error;
    }
  };
}

export function useWithTokenRefresh() {
  const { apiClient } = useApi();
  const { authState, setAuthState } = useAuth();
  const navigate = useNavigate();

  // Use useCallback to memoize the function
  return React.useCallback(
    async function withTokenRefresh<T>(apiCall: () => Promise<T>): Promise<T> {
      try {
        return await apiCall();
      } catch (error: any) {
        // When MFA verification is pending the token is temporary — don't try to
        // refresh it and don't navigate away, just let the error propagate so the
        // MFA flow can complete undisturbed.
        const isMfaPending = (() => { try { return sessionStorage.getItem('mfa_pending') === 'true'; } catch { return false; } })();
        if (isMfaPending) throw error;

        const is401 =
          (error instanceof HttpError && error.status === 401) ||
          (error &&
            typeof error === 'object' &&
            'status' in error &&
            (error as any).status === 401);
        if (is401 && authState?.refreshToken) {

          try {
            const newToken = await apiClient.refreshToken(
              authState.refreshToken,
              authState.provider || ApiInterfaces.ProviderType.GOOGLE
            );

            if (
              newToken &&
              'token' in newToken &&
              'accessToken' in newToken &&
              'refreshToken' in newToken
            ) {
              const { token, accessToken, refreshToken, expiresAt } = newToken;

              // Mettre à jour l'état d'authentification avec les nouveaux tokens
              const updatedAuthState = {
                ...authState,
                token: token,
                accessToken: accessToken,
                refreshToken: refreshToken,
                expiresAt: expiresAt,
              };
              setAuthState(updatedAuthState);
              // Utiliser le nouveau token pour l'API client, pas l'ancien
              apiClient.setAuthToken(token);
            } else {
              navigate('/');
              throw new Error('Authentication failed');
            }
            return await apiCall();
          } catch (refreshError) {
            navigate('/');
            throw new Error('Authentication failed');
          }
        }
        throw error;
      }
    },
    [apiClient, authState, navigate, setAuthState] // Dependencies for the callback
  );
}

export function processAuthError(errorCode: string): string {
  switch (errorCode) {
    case 'INVALID_EMAIL_DOMAIN':
      return 'The email domain is not valid. Please use a valid email domain.';
    case 'EMAIL_EXISTS_ACTIVE':
      return 'The email is already in use. Please use a different email.';
    case 'EMAIL_EXISTS_INACTIVE':
      return 'The email is already in use. Please use a different email.';
    case 'ORG_EXISTS':
      return 'The organization is already in use. Please use a different organization.';
    case 'ORG_DISABLED':
      return 'The organization exists but is disabled. Please contact support.';
    case 'USER_QUOTA_EXCEEDED':
      return 'The user quota has been exceeded. Please contact your organization administrator.';
    case 'WEAK_PASSWORD':
      return 'The password is too weak. Please use a stronger password.';
    case 'INVALID_INPUT':
      return 'Please check that all fields are correctly filled';
    case 'ACTIVATION_REQUIRED':
      return 'No account found. An activation link is required to register. Please request one from the homepage.';
    default:
      return 'Signup/authentication failed';
  }
}